import { sci as sciText } from '../core/sci'
/** Minimal element helper. Keeps the panel code readable without a framework. */

type Attrs = Record<string, string | number | boolean | EventListener | undefined>

export function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  attrs: Attrs = {},
  children: Array<Node | string | null | undefined> = [],
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag)
  for (const [k, v] of Object.entries(attrs)) {
    if (v === undefined || v === false) continue
    if (k.startsWith('on') && typeof v === 'function') {
      node.addEventListener(k.slice(2).toLowerCase(), v as EventListener)
    } else if (k === 'class') {
      node.className = String(v)
    } else if (k === 'text') {
      node.textContent = String(v)
    } else if (k === 'html') {
      node.innerHTML = String(v)
    } else {
      node.setAttribute(k, String(v))
    }
  }
  for (const c of children) {
    if (c === null || c === undefined) continue
    node.appendChild(typeof c === 'string' ? document.createTextNode(c) : c)
  }
  return node
}

export const clear = (node: HTMLElement) => {
  while (node.firstChild) node.removeChild(node.firstChild)
}

/** Format a number for a readout, choosing a sensible precision. */
export function fmt(v: number, unit = ''): string {
  if (!Number.isFinite(v)) return '--'
  const a = Math.abs(v)
  const s = a >= 1000 ? v.toFixed(0) : a >= 100 ? v.toFixed(0) : a >= 10 ? v.toFixed(1) : a >= 1 ? v.toFixed(2) : v.toFixed(3)
  return unit ? `${s} ${unit}` : s
}

/** Compact scientific notation for densities and fluxes. */
export const sci = (v: number, digits = 2): string => (Number.isFinite(v) ? sciText(v, digits) : '0')
